import * as React from 'react'

import { Form } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import {
  ListSelector,
  type ListSelectorTitleVariant,
} from '~/client/src/shared/components/ListSelector/ListSelector'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { countVisibleResourceInputs } from '~/client/src/shared/resources/resourcePropertyInput'
import { getSystemFormFieldKeys } from '~/client/src/shared/resources/systemFormFields'

import EmptyTemplatesState from './EmptyTemplatesState'
import {
  ResourceTemplate,
  templateIdOf,
  useResolvedTemplate,
} from './useResolvedTemplate'

interface FormItem {
  id: string
  name: string
  visibleCount: number
}

interface IBaseProps {
  title: string
  titleVariant?: ListSelectorTitleVariant
  resourceTypeId: string
  onClose: () => void
  onCreateNewForm?: () => void
  className?: string
  templates: ResourceTemplate[]
  loading: boolean
}

interface ISingleProps extends IBaseProps {
  mode?: 'single'
  onSelect: (templateId: string) => void
}

interface IMultiProps extends IBaseProps {
  mode: 'multi'
  selectedIds: string[]
  onApply: (templateIds: string[]) => void
  applyLabel?: string
}

type IProps = ISingleProps | IMultiProps

const EMPTY_SET = new Set<string>()

const FormListSelector: React.FC<IProps> = props => {
  const isMulti = props.mode === 'multi'
  const { title, resourceTypeId, onClose, onCreateNewForm, className } = props

  const { templates, isEmpty, loading } = useResolvedTemplate({
    enabled: true,
    chosenTemplateId: null,
    templates: props.templates,
    loading: props.loading,
  })

  const systemFieldCount = getSystemFormFieldKeys(resourceTypeId).length

  const items: FormItem[] = React.useMemo(
    () =>
      templates
        .map(t => {
          const id = templateIdOf(t)
          if (id == null) return null
          return {
            id,
            name: t.name || '',
            // The form editor always renders the system fields, so a form shows more
            // than its template stores: Name for every resource, plus Icon, Color and
            // Parent for Equipment.
            visibleCount: countVisibleResourceInputs(t) + systemFieldCount,
          }
        })
        .filter((item): item is FormItem => item !== null)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [templates, systemFieldCount],
  )

  const multiSelectedIds = isMulti ? props.selectedIds : null
  const selectedIdsSet = React.useMemo(
    () => (multiSelectedIds ? new Set(multiSelectedIds) : EMPTY_SET),
    [multiSelectedIds],
  )

  if (loading) {
    return (
      <div
        className={`${
          className ?? 'tw:w-full tw:h-full tw:bg-background'
        } tw:flex tw:flex-col tw:overflow-hidden`}
      >
        <ListSelector.Title
          title={title}
          onClose={onClose}
          variant={props.titleVariant}
        />
        <div className="tw:flex tw:items-center tw:justify-center tw:flex-1">
          <Spinner size="md" />
        </div>
      </div>
    )
  }

  if (isEmpty)
    return (
      <EmptyTemplatesState
        title={title}
        onClose={onClose}
        titleVariant={props.titleVariant}
      />
    )

  return (
    <ListSelector.Root<FormItem>
      items={items}
      selectedIds={selectedIdsSet}
      selectionMode={isMulti ? 'multi' : 'single'}
      draft={isMulti}
      onItemToggle={isMulti ? undefined : props.onSelect}
      search={{
        placeholder: Localization.translator.search,
        searchFn: (item, query) => item.name.toLowerCase().includes(query),
      }}
      className={className ?? 'tw:w-full tw:h-full tw:bg-background'}
    >
      <ListSelector.Title
        title={title}
        onClose={onClose}
        variant={props.titleVariant}
      />
      <ListSelector.Toolbar />
      <ListSelector.Counter formatTotal={Localization.translator.xFormsCount} />
      <ListSelector.List<FormItem>
        renderItemContent={item => (
          <>
            <Form className="tw:size-4 tw:text-muted-foreground tw:shrink-0" />
            <div className="tw:flex tw:flex-col tw:min-w-0">
              <span className="tw:truncate">{item.name}</span>
              <span className="tw:text-body-sm tw:text-muted-foreground">
                {item.visibleCount} {Localization.translator.propertiesPlural}
              </span>
            </div>
          </>
        )}
      />
      {isMulti ? (
        <ListSelector.Actions
          onApply={ids => props.onApply(Array.from(ids))}
          onClose={onClose}
          onCreateNew={onCreateNewForm}
          disableApplyWhenEmpty
          applyLabel={props.applyLabel}
        />
      ) : (
        onCreateNewForm && (
          <div className="tw:flex tw:items-center tw:p-3 tw:border-0 tw:border-t tw:border-solid tw:border-input-soft tw:shrink-0">
            <Button variant="outline" size="lg" onClick={onCreateNewForm}>
              <AddIcon data-icon="inline-start" size="xs" />
              {Localization.translator.createNew}
            </Button>
          </div>
        )
      )}
    </ListSelector.Root>
  )
}

export default FormListSelector
